import React, { useEffect, useState, useRef, Children, Component } from 'react';
import { motion, useInView } from 'framer-motion';
import { HeartPulseIcon, BrainIcon, ActivityIcon, ShieldCheckIcon, ScanIcon, SparklesIcon, ZapIcon, TargetIcon, ArrowRightIcon, QuoteIcon, MenuIcon, XIcon } from 'lucide-react';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
const fadeIn = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};
const scaleIn = {
  hidden: {
    opacity: 0,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    scale: 1
  }
};
// Reusable animated section wrapper
function AnimatedSection({
  children,
  className = '',
  delay = 0
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <motion.section ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={fadeInUp} transition={{
    duration: 0.7,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={className} data-id="element-0">
      {children}
    </motion.section>;
}
// Header Component
function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      setIsMobileMenuOpen(false);
    }
  };
  return <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-[#0A1628]/80 backdrop-blur-xl shadow-2xl border-b border-white/5' : 'bg-transparent'}`} data-id="element-1">
      <div className="max-w-7xl mx-auto px-6 py-5" data-id="element-2">
        <div className="flex items-center justify-between" data-id="element-3">
          {/* Logo/Brand */}
          <button onClick={() => scrollToSection('hero')} className="font-serif text-xl md:text-2xl font-semibold text-white hover:text-[#D4AF37] transition-all duration-300" data-id="element-4">
            Dr. Ali Mazloum
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10" data-id="element-5">
            <button onClick={() => scrollToSection('expertise')} className="text-white/80 hover:text-[#D4AF37] transition-all duration-300 text-sm font-medium tracking-wider relative group" data-id="element-6">
              Expertise
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full" data-id="element-7" />
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-white/80 hover:text-[#D4AF37] transition-all duration-300 text-sm font-medium tracking-wider relative group" data-id="element-8">
              Results
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#D4AF37] transition-all duration-300 group-hover:w-full" data-id="element-9" />
            </button>
            <button onClick={() => scrollToSection('booking')} className="bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0A1628] px-7 py-3 text-sm font-semibold rounded-sm hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all duration-300 hover:scale-105" data-id="element-10">
              Book Meeting
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden text-white p-2 hover:text-[#D4AF37] transition-colors" data-id="element-11">
            {isMobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-12" /> : <MenuIcon className="w-6 h-6" data-id="element-13" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <motion.div initial={{
      opacity: 0,
      y: -20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="md:hidden bg-[#0A1628]/95 backdrop-blur-xl border-t border-white/5" data-id="element-14">
          <nav className="flex flex-col px-6 py-6 gap-4" data-id="element-15">
            <button onClick={() => scrollToSection('expertise')} className="text-white/90 hover:text-[#D4AF37] transition-colors text-left py-3 font-medium border-b border-white/5" data-id="element-16">
              Expertise
            </button>
            <button onClick={() => scrollToSection('testimonials')} className="text-white/90 hover:text-[#D4AF37] transition-colors text-left py-3 font-medium border-b border-white/5" data-id="element-17">
              Results
            </button>
            <button onClick={() => scrollToSection('booking')} className="bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0A1628] px-6 py-3 font-semibold rounded-sm hover:shadow-lg transition-all text-center mt-2" data-id="element-18">
              Book Meeting
            </button>
          </nav>
        </motion.div>}
    </header>;
}
// Hero Section
function Hero() {
  return <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0A1628] via-[#0F1F3A] to-[#1A2A42]" data-id="element-19">
      {/* Animated gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl animate-pulse" data-id="element-20" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl animate-pulse" style={{
      animationDelay: '1s'
    }} data-id="element-21" />

      {/* Sophisticated grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: `linear-gradient(rgba(212, 175, 55, 0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(212, 175, 55, 0.3) 1px, transparent 1px)`,
      backgroundSize: '80px 80px'
    }} data-id="element-22" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 grid lg:grid-cols-2 gap-16 items-center" data-id="element-23">
        {/* Content */}
        <div className="text-center lg:text-left" data-id="element-24">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-25">
            <span className="inline-block text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-8 relative" data-id="element-26">
              <span className="absolute -left-8 top-1/2 w-6 h-px bg-[#D4AF37]" data-id="element-27" />
              Dr. Ali Mazloum
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-semibold text-white mb-8 tracking-tight leading-[1.1]" data-id="element-28">
            The Human MRI
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="text-xl md:text-2xl text-[#D4AF37] font-serif italic mb-10" data-id="element-29">
            Where Hospitals Fail, Mastery Prevails
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.8
        }} className="text-base md:text-lg text-gray-300 max-w-xl mx-auto lg:mx-0 mb-12 leading-relaxed font-light" data-id="element-30">
            The biomedical genius who operates far beyond the capabilities of
            conventional medicine. Where specialists fail and hospitals give up,
            Dr. Ali has diagnosed and solved cases considered "incurable."
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 1
        }} data-id="element-31">
            <a href="#booking" className="inline-flex items-center gap-3 bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0A1628] px-10 py-5 text-base font-semibold rounded-sm hover:shadow-2xl hover:shadow-[#D4AF37]/40 transition-all duration-300 hover:scale-105" data-id="element-32">
              Book Your Meeting
              <ArrowRightIcon className="w-5 h-5" data-id="element-33" />
            </a>
          </motion.div>
        </div>

        {/* Photo with premium treatment */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} transition={{
        duration: 1.2,
        delay: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="relative lg:justify-self-end" data-id="element-34">
          {/* Ambient glow layers */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/30 via-transparent to-[#D4AF37]/20 blur-3xl scale-110" data-id="element-35" />
          <div className="absolute -inset-8 bg-gradient-to-tr from-[#D4AF37]/10 to-transparent blur-2xl" data-id="element-36" />

          {/* Image container with sophisticated mask */}
          <div className="relative group" data-id="element-37">
            <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/20 to-transparent opacity-60 mix-blend-overlay rounded-lg" data-id="element-38" />
            <img src="https://cdn.magicpatterns.com/uploads/mtKQEMDDqqqPspJeED3CzH/DrAli1_-_tinyfied.png" alt="Dr. Ali Mazloum" className="relative w-full max-w-lg mx-auto rounded-lg shadow-2xl shadow-black/50 transition-transform duration-700 group-hover:scale-[1.02]" data-id="element-39" />
            {/* Subtle overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/40 via-transparent to-transparent rounded-lg pointer-events-none" data-id="element-40" />
          </div>
        </motion.div>
      </div>

      {/* Modern scroll indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.8,
      duration: 1
    }} className="absolute bottom-12 left-1/2 -translate-x-1/2" data-id="element-41">
        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2.5,
        repeat: Infinity,
        ease: 'easeInOut'
      }} className="flex flex-col items-center gap-2" data-id="element-42">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" data-id="element-43" />
          <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" data-id="element-44" />
        </motion.div>
      </motion.div>
    </section>;
}
// Stats Section with glassmorphism
function Stats() {
  const stats = [{
    number: '20+',
    label: 'Years of Expertise'
  }, {
    number: '80+',
    label: 'Peptides Mastered'
  }, {
    number: '1000+',
    label: 'Lives Transformed'
  }, {
    number: '∞',
    label: 'Impossible Cases Solved'
  }];
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  return <section className="relative bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4] py-24 border-y border-gray-200/50" data-id="element-45">
      {/* Subtle background accent */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(212,175,55,0.03),transparent_50%)]" data-id="element-46" />

      <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer} className="relative max-w-6xl mx-auto px-6" data-id="element-47">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12" data-id="element-48">
          {stats.map((stat, index) => <motion.div key={index} variants={fadeInUp} transition={{
          duration: 0.6
        }} className="text-center group" data-id="element-49">
              <div className="font-serif text-5xl md:text-6xl font-semibold bg-gradient-to-br from-[#0A1628] to-[#1A2A42] bg-clip-text text-transparent mb-3 transition-transform duration-300 group-hover:scale-110" data-id="element-50">
                {stat.number}
              </div>
              <div className="text-xs md:text-sm text-gray-600 tracking-wider uppercase font-medium" data-id="element-51">
                {stat.label}
              </div>
            </motion.div>)}
        </div>
      </motion.div>
    </section>;
}
// Expertise Section with premium cards
function Expertise() {
  const conditions = [{
    icon: HeartPulseIcon,
    title: 'Heart Failure',
    description: 'Reversed cases where transplants were the only option. Clients now live active, healthy lives.'
  }, {
    icon: ActivityIcon,
    title: 'Type 1 & 2 Diabetes',
    description: 'Complete reversal of both types through precise metabolic restoration protocols.'
  }, {
    icon: ShieldCheckIcon,
    title: 'Autoimmune Diseases',
    description: "Rheumatoid arthritis, Graves' disease, and immune collapse resolved at the root."
  }, {
    icon: BrainIcon,
    title: 'Neurological Disorders',
    description: 'Depression, anxiety, epilepsy, and cognitive decline addressed through advanced protocols.'
  }, {
    icon: ZapIcon,
    title: 'Kidney Disease',
    description: 'Restored kidney function in patients facing dialysis, avoiding the need entirely.'
  }, {
    icon: SparklesIcon,
    title: 'Chronic Pain',
    description: 'Eliminated chronic pain conditions that resisted all standard treatments.'
  }];
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="expertise" className="bg-gradient-to-b from-[#F5F5F4] to-[#FAFAF9] py-32 md:py-40" data-id="element-52">
      <div className="max-w-6xl mx-auto px-6" data-id="element-53">
        <AnimatedSection className="text-center mb-20" data-id="element-54">
          <span className="text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-6 block" data-id="element-55">
            Expertise
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-[#0A1628] mb-8 tracking-tight" data-id="element-56">
            Reversing the Impossible
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-57">
            Where conventional medicine declares defeat, Dr. Ali finds
            solutions. These are not improvements—they are complete reversals.
          </p>
        </AnimatedSection>

        <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-58">
          {conditions.map((condition, index) => <motion.div key={index} variants={scaleIn} transition={{
          duration: 0.5
        }} className="group relative bg-white/60 backdrop-blur-sm p-8 md:p-10 border border-gray-200/50 hover:border-[#D4AF37]/30 transition-all duration-500 hover:shadow-xl hover:shadow-[#D4AF37]/5 hover:-translate-y-1" data-id="element-59">
              {/* Subtle gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-60" />

              <div className="relative" data-id="element-61">
                <div className="w-14 h-14 bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-62">
                  <condition.icon className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} data-id="element-63" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-[#0A1628] mb-4" data-id="element-64">
                  {condition.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-65">
                  {condition.description}
                </p>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
// Differentiators Section
function Differentiators() {
  const points = [{
    icon: ScanIcon,
    title: 'Diagnostic Precision',
    description: 'Spots overlooked pathologies on MRI, EKG, and lab work within minutes. Sees what dozens of specialists have missed.'
  }, {
    icon: TargetIcon,
    title: 'Root Cause Focus',
    description: "Your body is not a collection of broken parts. It's a unified system. Dr. Ali fixes the core problem, not the symptoms."
  }, {
    icon: SparklesIcon,
    title: 'Peptide Mastery',
    description: 'Expert in 80+ peptides including the complete Khavinson library. Integrates them safely with advanced protocols.'
  }, {
    icon: ZapIcon,
    title: 'Performance Optimization',
    description: 'Deep expertise in hormones, testosterone, and performance-enhancing agents for unparalleled results in strength and vitality.'
  }];
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section className="relative bg-gradient-to-b from-[#0A1628] via-[#0F1F3A] to-[#0A1628] py-32 md:py-40 overflow-hidden" data-id="element-66">
      {/* Animated background elements */}
      <div className="absolute top-1/3 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" data-id="element-67" />
      <div className="absolute bottom-1/3 left-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl" data-id="element-68" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-69">
        <AnimatedSection className="text-center mb-20" data-id="element-70">
          <span className="text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-6 block" data-id="element-71">
            The Difference
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-8 tracking-tight" data-id="element-72">
            Beyond Conventional Medicine
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed" data-id="element-73">
            Elite entrepreneurs, CEOs, athletes, and even physicians themselves
            come to Dr. Ali when nobody else can figure out what's wrong.
          </p>
        </AnimatedSection>

        <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer} className="grid md:grid-cols-2 gap-8 md:gap-10" data-id="element-74">
          {points.map((point, index) => <motion.div key={index} variants={fadeInUp} transition={{
          duration: 0.6
        }} className="flex gap-6 group" data-id="element-75">
              <div className="flex-shrink-0" data-id="element-76">
                <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="element-77">
                  <point.icon className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} data-id="element-78" />
                </div>
              </div>
              <div data-id="element-79">
                <h3 className="font-serif text-xl md:text-2xl font-semibold text-white mb-4" data-id="element-80">
                  {point.title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-base" data-id="element-81">
                  {point.description}
                </p>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
// In Practice Section with cinematic image treatment
function InPractice() {
  return <section className="relative bg-[#0A1628] py-32 md:py-40 overflow-hidden" data-id="element-82">
      {/* Ambient lighting */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0F1F3A] to-[#0A1628]" data-id="element-83" />

      <div className="relative max-w-6xl mx-auto px-6" data-id="element-84">
        <AnimatedSection className="text-center mb-16" data-id="element-85">
          <span className="text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-6 block" data-id="element-86">
            In Practice
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-8 tracking-tight" data-id="element-87">
            Where Medicine Meets Mastery
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed" data-id="element-88">
            In one of Russia's largest hospitals, Dr. Ali is consulted on the
            cases that stump entire teams of specialists.
          </p>
        </AnimatedSection>

        <AnimatedSection delay={0.2} data-id="element-89">
          <div className="relative group" data-id="element-90">
            {/* Sophisticated glow effect */}
            <div className="absolute -inset-8 bg-gradient-to-r from-[#D4AF37]/20 via-[#D4AF37]/10 to-[#D4AF37]/20 blur-3xl opacity-50 group-hover:opacity-70 transition-opacity duration-700" data-id="element-91" />

            {/* Image with cinematic treatment */}
            <div className="relative overflow-hidden rounded-lg" data-id="element-92">
              <img src="https://cdn.magicpatterns.com/uploads/7T6iRWJJGUCHY78D8N5oMZ/Generated_Image_December_04,_2025_-_2_31PM_-tinyfie.jpg" alt="Dr. Ali Mazloum working with patients in hospital" className="relative w-full shadow-2xl shadow-black/50 transition-transform duration-700 group-hover:scale-105" data-id="element-93" />
              {/* Cinematic gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/60 via-transparent to-transparent pointer-events-none" data-id="element-94" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37]/5 to-transparent mix-blend-overlay pointer-events-none" data-id="element-95" />
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}
// Testimonials Section with premium cards
function Testimonials() {
  const testimonials = [{
    quote: "As a doctor myself, I was skeptical of anything outside standard cardiology protocols. But conventional medicine had nothing left to offer me except a new heart. What you've done isn't just impressive—it's life-changing.",
    author: 'Glen S.',
    role: 'Physician'
  }, {
    quote: 'Ali... You have done what our entire team of medical professionals were not able to do.',
    author: 'Badar A.',
    role: 'Client'
  }, {
    quote: 'I was at rock bottom. Doctors, meds, nothing worked... This… it gave me my life back.',
    author: 'Nick B.',
    role: 'Transformation Client'
  }, {
    quote: "I've gone from thinking about my funeral to planning hiking trips. I can't thank you guys and the team enough.",
    author: 'Tony R.',
    role: 'Transformation Client'
  }];
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="testimonials" className="bg-gradient-to-b from-[#F5F5F4] to-[#FAFAF9] py-32 md:py-40" data-id="element-96">
      <div className="max-w-6xl mx-auto px-6" data-id="element-97">
        <AnimatedSection className="text-center mb-20" data-id="element-98">
          <span className="text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-6 block" data-id="element-99">
            Testimonials
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-[#0A1628] mb-8 tracking-tight" data-id="element-100">
            Transformations That Speak
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" data-id="element-101">
            Real results from real people who experienced what modern medicine
            still calls impossible.
          </p>
        </AnimatedSection>

        <motion.div ref={ref} initial="hidden" animate={isInView ? 'visible' : 'hidden'} variants={staggerContainer} className="grid md:grid-cols-2 gap-8" data-id="element-102">
          {testimonials.map((testimonial, index) => <motion.div key={index} variants={scaleIn} transition={{
          duration: 0.5
        }} className="group relative bg-white/80 backdrop-blur-sm p-10 md:p-12 border border-gray-200/50 hover:border-[#D4AF37]/30 transition-all duration-500 hover:shadow-2xl hover:shadow-[#D4AF37]/5 hover:-translate-y-1" data-id="element-103">
              {/* Subtle accent */}
              <div className="absolute top-0 left-0 w-16 h-1 bg-gradient-to-r from-[#D4AF37] to-transparent" data-id="element-104" />

              <QuoteIcon className="w-10 h-10 text-[#D4AF37]/20 absolute top-8 right-8 group-hover:scale-110 transition-transform duration-300" data-id="element-105" />
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8 relative z-10 font-light" data-id="element-106">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-4" data-id="element-107">
                <div className="w-14 h-14 bg-gradient-to-br from-[#0A1628] to-[#1A2A42] rounded-full flex items-center justify-center shadow-lg" data-id="element-108">
                  <span className="text-white font-semibold text-lg" data-id="element-109">
                    {testimonial.author.charAt(0)}
                  </span>
                </div>
                <div data-id="element-110">
                  <div className="font-semibold text-[#0A1628] text-base" data-id="element-111">
                    {testimonial.author}
                  </div>
                  <div className="text-sm text-gray-500" data-id="element-112">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}
// Approach Section with premium image treatment
function Approach() {
  return <section className="bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4] py-32 md:py-40" data-id="element-113">
      <div className="max-w-6xl mx-auto px-6" data-id="element-114">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-115">
          {/* Photo with floating effect */}
          <AnimatedSection data-id="element-116">
            <div className="relative group" data-id="element-117">
              {/* Ambient glow */}
              <div className="absolute -inset-8 bg-gradient-to-br from-[#D4AF37]/20 via-[#D4AF37]/10 to-transparent blur-3xl opacity-60 group-hover:opacity-80 transition-opacity duration-700" data-id="element-118" />

              {/* Image container */}
              <div className="relative overflow-hidden rounded-lg" data-id="element-119">
                <img src="https://cdn.magicpatterns.com/uploads/1QtFfTgS5AwbxfvfyNSc1E/Generated_Image_December_04,_2025_-_2_29PM_-_tinyfied.jpg" alt="Dr. Ali Mazloum - Professional Portrait" className="relative w-full shadow-2xl shadow-black/20 transition-transform duration-700 group-hover:scale-105" data-id="element-120" />
                {/* Sophisticated overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/10 via-transparent to-transparent mix-blend-overlay pointer-events-none" data-id="element-121" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628]/20 via-transparent to-transparent pointer-events-none" data-id="element-122" />
              </div>
            </div>
          </AnimatedSection>

          {/* Content */}
          <div data-id="element-123">
            <AnimatedSection data-id="element-124">
              <span className="text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-6 block" data-id="element-125">
                The Approach
              </span>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold text-[#0A1628] mb-10 tracking-tight leading-[1.1]" data-id="element-126">
                Precision Diagnostics,
                <br data-id="element-127" />
                Transformative Results
              </h2>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="space-y-6 text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-128">
              <p data-id="element-129">
                Working in one of the largest hospitals in Russia, physicians
                bring Dr. Ali "impossible cases" because he sees what dozens of
                others have missed—sometimes within minutes.
              </p>
              <p data-id="element-130">
                He has routinely spotted overlooked pathologies on MRI, EKG, and
                lab work, then designed treatment strategies that actually work.
                His approach treats the body as a single, integrated, unified
                system where everything affects everything else.
              </p>
              <p data-id="element-131">
                This isn't about managing symptoms or playing endless
                whack-a-mole with your health. It's about fixing the real
                problem at the source—deep inside your body's cells.
              </p>
              <p className="text-[#0A1628] font-medium text-lg" data-id="element-132">
                Because your job is not to "manage" your health problems. Your
                job is to make it so you don't have health problems.
              </p>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>;
}
// CTA Section with premium styling
function CTASection() {
  return <section id="booking" className="relative bg-gradient-to-b from-[#0A1628] via-[#0F1F3A] to-[#0A1628] py-32 md:py-40 overflow-hidden" data-id="element-133">
      {/* Sophisticated background elements */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#D4AF37]/10 rounded-full blur-3xl" data-id="element-134" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-32 bg-gradient-to-b from-transparent via-[#D4AF37]/50 to-transparent" data-id="element-135" />

      <div className="relative max-w-4xl mx-auto px-6 text-center z-10" data-id="element-136">
        <AnimatedSection data-id="element-137">
          <span className="text-[#D4AF37] text-xs font-medium tracking-[0.4em] uppercase mb-6 block" data-id="element-138">
            Your Transformation Begins
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold text-white mb-8 tracking-tight" data-id="element-139">
            Meeting With the Master
          </h2>
          <p className="text-xl md:text-2xl text-[#D4AF37] font-serif italic mb-12" data-id="element-140">
            "Change Your Life In A Single Hour"
          </p>
        </AnimatedSection>

        <AnimatedSection delay={0.2} className="mb-16" data-id="element-141">
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-142">
            In this single, powerful session, you will get what years of chasing
            symptoms has never provided:{' '}
            <span className="text-white font-medium" data-id="element-143">clarity</span>.
          </p>

          <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-10 md:p-12 max-w-xl mx-auto mb-12 rounded-lg" data-id="element-144">
            <h3 className="text-white font-semibold text-lg mb-8" data-id="element-145">
              What You'll Receive:
            </h3>
            <ul className="text-left text-gray-300 space-y-5" data-id="element-146">
              <li className="flex items-start gap-4" data-id="element-147">
                <span className="text-[#D4AF37] mt-1 text-lg" data-id="element-148">✓</span>
                <span data-id="element-149">Root cause analysis of what's holding you back</span>
              </li>
              <li className="flex items-start gap-4" data-id="element-150">
                <span className="text-[#D4AF37] mt-1 text-lg" data-id="element-151">✓</span>
                <span data-id="element-152">Step-by-step breakdown of what you need to do next</span>
              </li>
              <li className="flex items-start gap-4" data-id="element-153">
                <span className="text-[#D4AF37] mt-1 text-lg" data-id="element-154">✓</span>
                <span data-id="element-155">
                  Exact protocols: what to take, how, when, and where to get it
                </span>
              </li>
              <li className="flex items-start gap-4" data-id="element-156">
                <span className="text-[#D4AF37] mt-1 text-lg" data-id="element-157">✓</span>
                <span data-id="element-158">Complete clarity on your path to transformation</span>
              </li>
            </ul>
          </div>

          <div className="mb-12" data-id="element-159">
            <div className="text-6xl md:text-7xl font-serif font-semibold bg-gradient-to-br from-white to-gray-300 bg-clip-text text-transparent mb-3" data-id="element-160">
              $1,500
            </div>
            <div className="text-gray-400 text-base" data-id="element-161">
              Single powerful session
            </div>
          </div>

          <a href="#" className="inline-flex items-center gap-3 bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0A1628] px-12 py-6 text-lg font-semibold rounded-sm hover:shadow-2xl hover:shadow-[#D4AF37]/40 transition-all duration-300 hover:scale-105" data-id="element-162">
            Book Your Meeting Now
            <ArrowRightIcon className="w-5 h-5" data-id="element-163" />
          </a>

          <p className="text-gray-500 text-sm mt-10" data-id="element-164">
            Full transformation programs available: $15,000 – $120,000
          </p>
        </AnimatedSection>
      </div>
    </section>;
}
// Footer
function Footer() {
  return <footer className="bg-[#0A1628] border-t border-white/5 py-16" data-id="element-165">
      <div className="max-w-6xl mx-auto px-6" data-id="element-166">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8" data-id="element-167">
          <div className="text-center md:text-left" data-id="element-168">
            <div className="font-serif text-2xl text-white mb-2" data-id="element-169">
              Dr. Ali Mazloum
            </div>
            <div className="text-[#D4AF37] text-sm tracking-widest" data-id="element-170">
              The Human MRI
            </div>
          </div>

          <div className="text-gray-500 text-sm" data-id="element-171">
            © {new Date().getFullYear()} All rights reserved
          </div>
        </div>
      </div>
    </footer>;
}
// Main App Component
export function App() {
  return <div className="min-h-screen bg-[#FAFAF9]" data-id="element-172">
      <Header data-id="element-173" />
      <Hero data-id="element-174" />
      <Stats data-id="element-175" />
      <Expertise data-id="element-176" />
      <Differentiators data-id="element-177" />
      <InPractice data-id="element-178" />
      <Testimonials data-id="element-179" />
      <Approach data-id="element-180" />
      <CTASection data-id="element-181" />
      <Footer data-id="element-182" />
    </div>;
}